// Sugalarm — Ambulatory Glucose Profile (AGP) panel (cycle 177).
//
// The clinician-standard percentile chart: every day's readings in the
// window are folded onto a single 24-hour TIME-OF-DAY axis and reduced to
// percentile bands per half-hour bin — median (p50), the 25–75% (IQR)
// band, and the 5–95% band — drawn over the 70–180 mg/dL target band.
//
// Data path: computed CLIENT-SIDE from the readings the dashboard already
// loads for the selected window — no second fetch, no new endpoint. The
// bins are LOCAL time-of-day by construction (we read each reading's
// local hour/minute via the browser clock), so the deferred server-side
// per-user timezone is a non-issue here.
//
// WEB FRAME ONLY in practice — the dashboard that mounts this is web-
// gated. Every fill is a CSS token (`--agp-*`, `--zone-ok-soft`) so the
// bands re-tint cleanly when the web light/dark theme toggles.

import { useMemo } from "react";

import { displayValue } from "../../lib/glucose";
import type { Reading, Thresholds } from "../../lib/types";
import styles from "./AgpChart.module.css";

// 48 half-hour bins across the 24-hour day.
const BIN_COUNT = 48;
const BIN_MINUTES = (24 * 60) / BIN_COUNT;
// AGP is only meaningful once ~2 weeks of days are present; below this we
// show an explicit empty state instead of a misleading sparse chart.
const MIN_DAYS = 14;
// A bin needs at least this many samples before we trust its percentiles.
const MIN_BIN_SAMPLES = 3;

// Fixed viewBox; the SVG scales to its container width (Sparkline/History
// convention). `preserveAspectRatio="none"`, width:100%.
const VIEW_W = 960;
const PAD_L = 34;
const PAD_R = 12;
const PAD_T = 10;
const PAD_B = 22;

const Y_FLOOR = 40;
const Y_CEIL = 400;

interface BinStats {
  i: number;
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
}

export interface AgpData {
  bins: BinStats[];
  dayCount: number;
  readingCount: number;
}

/** Linear-interpolated percentile (q in [0,1]) over a pre-sorted array. */
function percentile(sorted: number[], q: number): number {
  const n = sorted.length;
  if (n === 0) return NaN;
  if (n === 1) return sorted[0]!;
  const idx = q * (n - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  const frac = idx - lo;
  return sorted[lo]! * (1 - frac) + sorted[hi]! * frac;
}

/** Fold the window's readings onto 48 local-time-of-day bins and reduce
 *  each to its p5/p25/p50/p75/p95. Distinct LOCAL calendar days are
 *  counted so the caller can gate the empty state. */
export function computeAgp(readings: Reading[]): AgpData {
  const buckets: number[][] = Array.from({ length: BIN_COUNT }, () => []);
  const days = new Set<string>();
  for (const r of readings) {
    const d = new Date(r.timestamp);
    const ms = d.getTime();
    if (Number.isNaN(ms)) continue;
    // Local time-of-day → bin. getHours/getMinutes are the browser's
    // local clock, so the fold is local without any tz plumbing.
    const minuteOfDay = d.getHours() * 60 + d.getMinutes();
    let bin = Math.floor(minuteOfDay / BIN_MINUTES);
    if (bin < 0) bin = 0;
    if (bin >= BIN_COUNT) bin = BIN_COUNT - 1;
    buckets[bin]!.push(r.sgv);
    days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }

  const bins: BinStats[] = [];
  for (let i = 0; i < BIN_COUNT; i++) {
    const vals = buckets[i]!;
    if (vals.length < MIN_BIN_SAMPLES) continue;
    const sorted = [...vals].sort((a, b) => a - b);
    bins.push({
      i,
      p5: percentile(sorted, 0.05),
      p25: percentile(sorted, 0.25),
      p50: percentile(sorted, 0.5),
      p75: percentile(sorted, 0.75),
      p95: percentile(sorted, 0.95),
    });
  }

  return { bins, dayCount: days.size, readingCount: readings.length };
}

interface Props {
  readings: Reading[];
  thresholds: Thresholds;
  units: "mg/dL" | "mmol/L";
  height?: number;
}

export function AgpChart({ readings, thresholds, units, height = 300 }: Props) {
  const data = useMemo(() => computeAgp(readings), [readings]);

  const H = height;
  const innerW = VIEW_W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  // Y domain: span the data's p5..p95 plus the target band, padded and
  // clamped so one extreme bin can't squash the rest.
  const { yMin, yMax } = useMemo(() => {
    let lo = thresholds.target_low;
    let hi = thresholds.target_high;
    for (const b of data.bins) {
      if (b.p5 < lo) lo = b.p5;
      if (b.p95 > hi) hi = b.p95;
    }
    lo = Math.max(Y_FLOOR, Math.floor((lo - 10) / 20) * 20);
    hi = Math.min(Y_CEIL, Math.ceil((hi + 10) / 20) * 20);
    if (hi - lo < 80) hi = lo + 80;
    return { yMin: lo, yMax: hi };
  }, [data.bins, thresholds.target_low, thresholds.target_high]);

  // Bin center → x; value → y.
  const xAt = (binIndex: number) =>
    PAD_L + ((binIndex + 0.5) / BIN_COUNT) * innerW;
  const yAt = (v: number) =>
    PAD_T + (1 - (v - yMin) / (yMax - yMin)) * innerH;

  // Build filled area + median paths, broken across any gaps in coverage
  // so a missing bin doesn't draw a band through empty time.
  const { outerPaths, iqrPaths, medianPaths } = useMemo(() => {
    const runs: BinStats[][] = [];
    let cur: BinStats[] = [];
    for (const b of data.bins) {
      if (cur.length === 0 || b.i === cur[cur.length - 1]!.i + 1) {
        cur.push(b);
      } else {
        runs.push(cur);
        cur = [b];
      }
    }
    if (cur.length) runs.push(cur);

    const areaPath = (
      run: BinStats[],
      hi: (b: BinStats) => number,
      lo: (b: BinStats) => number,
    ): string => {
      if (run.length === 0) return "";
      const top = run
        .map(
          (b, k) =>
            `${k === 0 ? "M" : "L"}${xAt(b.i).toFixed(1)} ${yAt(hi(b)).toFixed(1)}`,
        )
        .join(" ");
      const bottom = [...run]
        .reverse()
        .map((b) => `L${xAt(b.i).toFixed(1)} ${yAt(lo(b)).toFixed(1)}`)
        .join(" ");
      return `${top} ${bottom} Z`;
    };

    const linePath = (run: BinStats[]): string =>
      run
        .map(
          (b, k) =>
            `${k === 0 ? "M" : "L"}${xAt(b.i).toFixed(1)} ${yAt(b.p50).toFixed(1)}`,
        )
        .join(" ");

    return {
      outerPaths: runs.map((r) => areaPath(r, (b) => b.p95, (b) => b.p5)),
      iqrPaths: runs.map((r) => areaPath(r, (b) => b.p75, (b) => b.p25)),
      medianPaths: runs.map((r) => linePath(r)),
    };
    // xAt/yAt close over yMin/yMax/innerW/innerH which are derived below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.bins, yMin, yMax, innerW, innerH, H]);

  // Insufficient-data state — AGP needs ~14 days to mean anything.
  if (data.dayCount < MIN_DAYS || data.bins.length === 0) {
    return (
      <div className={styles.empty} style={{ minHeight: height }}>
        <div className={styles.emptyTitle}>Not enough data yet</div>
        <div className={styles.emptySub}>
          The glucose profile needs about {MIN_DAYS} days of readings to be
          meaningful. {data.dayCount > 0
            ? `So far this window has ${data.dayCount} ${
                data.dayCount === 1 ? "day" : "days"
              } — pick a longer window or check back as more data arrives.`
            : "Pick a longer window or check back as more data arrives."}
        </div>
      </div>
    );
  }

  // Target band + threshold y-positions.
  const targetTopY = yAt(thresholds.target_high);
  const targetBotY = yAt(thresholds.target_low);

  const yTicks = Array.from(
    new Set([
      yMin,
      thresholds.target_low,
      thresholds.target_high,
      yMax,
    ]),
  )
    .filter((v) => v >= yMin && v <= yMax)
    .sort((a, b) => a - b);

  // Six x labels across the day (every 4 hours).
  const xTicks = [0, 4, 8, 12, 16, 20, 24].map((h) => {
    const minutes = h * 60;
    const x = PAD_L + (minutes / (24 * 60)) * innerW;
    const label =
      h === 0 || h === 24
        ? "12a"
        : h === 12
        ? "12p"
        : h < 12
        ? `${h}a`
        : `${h - 12}p`;
    return { x, label, h };
  });

  const yPct = (v: number) => (yAt(v) / H) * 100;
  const xPct = (x: number) => (x / VIEW_W) * 100;

  return (
    <div className={styles.wrap}>
      <svg
        className={styles.svg}
        height={H}
        viewBox={`0 0 ${VIEW_W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Ambulatory glucose profile: median, 25 to 75 percent, and 5 to 95 percent bands by time of day"
      >
        {/* Target band (70–180 mg/dL by default). */}
        <rect
          x={PAD_L}
          y={targetTopY}
          width={innerW}
          height={Math.max(0, targetBotY - targetTopY)}
          fill="var(--zone-ok-soft)"
        />
        <line
          x1={PAD_L}
          y1={targetTopY}
          x2={PAD_L + innerW}
          y2={targetTopY}
          stroke="var(--zone-ok)"
          strokeOpacity={0.45}
          strokeDasharray="2 4"
        />
        <line
          x1={PAD_L}
          y1={targetBotY}
          x2={PAD_L + innerW}
          y2={targetBotY}
          stroke="var(--zone-ok)"
          strokeOpacity={0.45}
          strokeDasharray="2 4"
        />

        {/* Hourly-ish vertical gridlines at the x ticks. */}
        {xTicks.map((t) => (
          <line
            key={`xg-${t.h}`}
            x1={t.x}
            y1={PAD_T}
            x2={t.x}
            y2={H - PAD_B}
            stroke="var(--ink-4)"
            strokeOpacity={0.25}
            strokeWidth={1}
          />
        ))}

        {/* 5–95% band (outer, lightest). */}
        {outerPaths.map((d, k) => (
          <path key={`o-${k}`} d={d} fill="var(--agp-band-outer)" />
        ))}
        {/* 25–75% IQR band (denser). */}
        {iqrPaths.map((d, k) => (
          <path key={`q-${k}`} d={d} fill="var(--agp-band-iqr)" />
        ))}
        {/* Median (p50) line. */}
        {medianPaths.map((d, k) => (
          <path
            key={`m-${k}`}
            d={d}
            fill="none"
            stroke="var(--agp-median)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      </svg>

      {/* HTML axis labels overlaid on the SVG. */}
      <div className={styles.axisLayer} aria-hidden="true">
        {yTicks.map((v) => (
          <span
            key={`y-${v}`}
            className={styles.yLabel}
            style={{ top: `${yPct(v)}%` }}
          >
            {displayValue(v, units)}
          </span>
        ))}
        {xTicks.map((t) => (
          <span
            key={`x-${t.h}`}
            className={styles.xLabel}
            style={{
              left: `${xPct(t.x)}%`,
              transform:
                t.h === 0
                  ? "translate(0, 0)"
                  : t.h === 24
                  ? "translate(-100%, 0)"
                  : "translate(-50%, 0)",
            }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {/* Legend. */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.swatchLine} />
          Median
        </span>
        <span className={styles.legendItem}>
          <span
            className={styles.swatch}
            style={{ background: "var(--agp-band-iqr)" }}
          />
          25–75%
        </span>
        <span className={styles.legendItem}>
          <span
            className={styles.swatch}
            style={{ background: "var(--agp-band-outer)" }}
          />
          5–95%
        </span>
        <span className={styles.legendItem}>
          <span
            className={styles.swatch}
            style={{ background: "var(--zone-ok-soft)" }}
          />
          Target {displayValue(thresholds.target_low, units)}–
          {displayValue(thresholds.target_high, units)}
        </span>
        <span className={styles.legendItem}>
          {data.dayCount} days · {data.readingCount.toLocaleString()} readings
        </span>
      </div>
    </div>
  );
}
